import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, Button, SectionTitle, SectionSubtitle } from '../../styles/StyledComponents';

const CTASection = styled(Section)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #e04a18 100%);
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
`;

const CTAContainer = styled(Container)`
  max-width: 800px;
`;

const OfferList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const OfferItem = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.5rem;
  width: 250px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const OfferIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.white};
  
  .material-icons {
    font-size: 2.5rem;
  }
`;

const OfferTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const OfferDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const CTAButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.1rem;
  padding: 1rem 2rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const CTATitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled(motion.p)`
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const CTAGuarantee = styled(motion.div)`
  margin-top: 2rem;
`;

const CTAGuaranteeTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAGuaranteeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CTAGuaranteeItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const CTAGuaranteeIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`;

const CTAGuaranteeText = styled.span`
  font-size: 1.1rem;
`;

const offers = [
  {
    icon: "analytics",
    title: "Free AI Readiness Assessment ($1,500 value)",
    description: "Discover your organization's automation potential with our comprehensive analysis."
  },
  {
    icon: "map",
    title: "Custom Automation Roadmap",
    description: "Get a tailored plan for implementing AI solutions that align with your business goals."
  },
  {
    icon: "trending_up",
    title: "ROI Projection Report",
    description: "See the projected financial impact of AI automation on your business operations."
  }
];

const CallToAction = () => {
  return (
    <CTASection id="cta">
      <CTAContainer>
        <CTATitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Ready to Transform Your Business with AI Automation?
        </CTATitle>
        <CTADescription
          as={motion.p}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Schedule your free consultation today and discover how TechRamp can help you automate processes, reduce costs, and drive growth.
        </CTADescription>
        
        <CTAGuarantee
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <CTAGuaranteeTitle>Our Promise to You:</CTAGuaranteeTitle>
          <CTAGuaranteeList>
            <CTAGuaranteeItem>
              <CTAGuaranteeIcon>✓</CTAGuaranteeIcon>
              <CTAGuaranteeText>100% satisfaction guarantee on deliverables</CTAGuaranteeText>
            </CTAGuaranteeItem>
            <CTAGuaranteeItem>
              <CTAGuaranteeIcon>✓</CTAGuaranteeIcon>
              <CTAGuaranteeText>Transparent pricing and milestones</CTAGuaranteeText>
            </CTAGuaranteeItem>
            <CTAGuaranteeItem>
              <CTAGuaranteeIcon>✓</CTAGuaranteeIcon>
              <CTAGuaranteeText>Regular performance reporting</CTAGuaranteeText>
            </CTAGuaranteeItem>
          </CTAGuaranteeList>
        </CTAGuarantee>
        
        <OfferList>
          {offers.map((offer, index) => (
            <OfferItem
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <OfferIcon>
                <span className="material-icons">{offer.icon}</span>
              </OfferIcon>
              <OfferTitle>{offer.title}</OfferTitle>
              <OfferDescription>{offer.description}</OfferDescription>
            </OfferItem>
          ))}
        </OfferList>
        
        <CTAButton 
          as="a" 
          href="https://cal.com/scott-laughlin-i6yml3/30min" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          Schedule Your Free Consultation
        </CTAButton>
      </CTAContainer>
    </CTASection>
  );
};

export default CallToAction;
